import { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import {
  LEDGER_STATUS_LABELS,
  ledgerRowIgnoredRedStatusIds,
  ledgerRowRedStatusIds,
  type LedgerRedStatusId,
} from '@/components/ledgerFilters'
import { ignoreWarehouseLedgerFlag, restoreWarehouseLedgerFlag } from '@/services/warehouse'
import type { WarehouseLedgerTransactionItem } from '@/services/warehouseTypes'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'

const DEFAULT_FLAG_DESCRIPTION = 'Data-quality check failed for this row.'

const LEDGER_FLAG_DESCRIPTIONS: Partial<Record<LedgerRedStatusId, string>> = {
  missing_linked_invoice: 'No product invoice line is linked to this manufacturer purchase.',
  missing_case_cost: 'Unit case cost is missing for this transaction.',
  missing_pick_fee_alloc: 'Pick fee allocation is missing for this store draw.',
  missing_receiving_alloc: 'Receiving allocation is missing for this inbound transaction.',
  missing_shipping_alloc: 'Shipping allocation is missing for this store draw.',
  unresolved_sku: 'SKU could not be resolved to a known product.',
  negative_balance:
    'Running on-hand cases for this SKU went below zero at this transaction — something upstream is likely incorrect.',
}

function flagDescription(flagId: LedgerRedStatusId): string {
  return LEDGER_FLAG_DESCRIPTIONS[flagId] ?? DEFAULT_FLAG_DESCRIPTION
}

type WarehouseLedgerFlagsModalProps = {
  row: WarehouseLedgerTransactionItem
  onClose: () => void
  onRowUpdated: (updated: WarehouseLedgerTransactionItem) => void
}

export default function WarehouseLedgerFlagsModal({
  row,
  onClose,
  onRowUpdated,
}: WarehouseLedgerFlagsModalProps) {
  const [currentRow, setCurrentRow] = useState(row)
  const [busyFlagId, setBusyFlagId] = useState<string | null>(null)

  useEffect(() => {
    setCurrentRow(row)
  }, [row])

  const activeIds = ledgerRowRedStatusIds(currentRow)
  const ignoredIds = ledgerRowIgnoredRedStatusIds(currentRow)

  async function handleIgnore(flagId: LedgerRedStatusId) {
    if (busyFlagId) return
    setBusyFlagId(flagId)
    try {
      const updated = await ignoreWarehouseLedgerFlag(currentRow.bk_txn_id, flagId)
      setCurrentRow(updated)
      onRowUpdated(updated)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to ignore flag'))
    } finally {
      setBusyFlagId(null)
    }
  }

  async function handleRestore(flagId: LedgerRedStatusId) {
    if (busyFlagId) return
    setBusyFlagId(flagId)
    try {
      const updated = await restoreWarehouseLedgerFlag(currentRow.bk_txn_id, flagId)
      setCurrentRow(updated)
      onRowUpdated(updated)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to restore flag'))
    } finally {
      setBusyFlagId(null)
    }
  }

  function renderFlagRow(
    flagId: LedgerRedStatusId,
    action: 'ignore' | 'restore'
  ) {
    const busy = busyFlagId === flagId
    const disabled = busyFlagId != null
    return (
      <li
        key={flagId}
        className="flex items-start justify-between gap-3 rounded-md border border-gray-100 bg-gray-50/60 px-3 py-2.5"
      >
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">{LEDGER_STATUS_LABELS[flagId]}</p>
          <p className="mt-0.5 text-xs text-gray-600">{flagDescription(flagId)}</p>
        </div>
        <button
          type="button"
          className="btn btn-secondary h-7 shrink-0 px-2.5 text-xs"
          disabled={disabled}
          onClick={() => void (action === 'ignore' ? handleIgnore(flagId) : handleRestore(flagId))}
        >
          {busy ? (action === 'ignore' ? 'Ignoring…' : 'Restoring…') : action === 'ignore' ? 'Ignore' : 'Restore'}
        </button>
      </li>
    )
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Row flags</h3>
            <p className="mt-1 text-xs text-gray-500">
              {currentRow.txn_date}
              {currentRow.bk_sku ? ` · ${currentRow.bk_sku}` : ''}
              {` · BK txn ${currentRow.bk_txn_id.slice(0, 8)}…`}
            </p>
          </div>
          <button
            type="button"
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
            onClick={onClose}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 min-h-0 flex-1 space-y-5 overflow-y-auto">
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Active</h4>
            {activeIds.length === 0 ? (
              <p className="mt-2 text-sm text-gray-600">
                All data-quality flags ignored for this row.
              </p>
            ) : (
              <ul className="mt-2 space-y-2">
                {activeIds.map((flagId) => renderFlagRow(flagId, 'ignore'))}
              </ul>
            )}
          </section>

          {ignoredIds.length > 0 ? (
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Ignored
              </h4>
              <ul className="mt-2 space-y-2">
                {ignoredIds.map((flagId) => renderFlagRow(flagId, 'restore'))}
              </ul>
            </section>
          ) : null}
        </div>

        <div className="mt-5 flex justify-end">
          <button type="button" className="btn btn-secondary text-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
